import { supabase, handleSupabaseError } from '../lib/supabase'
import { retryOperation } from '../utils/retryUtils'
import { evaluateAnswer, getNextAttemptNumber } from '../utils/calculations'

export const assessmentService = {
  // Start a new assessment session
  async startSession(userEmail, courseId) {
    try {
      // Get the attempt number
      const { data: previousAttempts, error: countError } = await supabase
        .from('assessment_sessions')
        .select('attempt_number')
        .eq('user_email', userEmail)
        .eq('course_id', courseId)
        .order('attempt_number', { ascending: false })
        .limit(1)
      
      if (countError) throw countError

      const attemptNumber = getNextAttemptNumber(previousAttempts)
      
      // Create new session
      const { data, error } = await supabase
        .from('assessment_sessions')
        .insert([{
          user_email: userEmail,
          course_id: courseId,
          attempt_number: attemptNumber,
          status: 'in_progress',
          phase: 'initial'
        }])
        .select()
        .single()

      if (error) throw error

      // CRITICAL FIX (GAP 3.2): Reset enrollment for retakes
      // When attempt_number > 1, update enrollment to in_progress so:
      // - Dashboard shows course under "Requires Attention"
      // - CourseCard resume logic works correctly
      if (attemptNumber > 1) {
        if (import.meta.env.DEV) console.log(`[startSession] Resetting enrollment for retake attempt ${attemptNumber}`)

        const { error: enrollmentError } = await supabase
          .from('enrollments')
          .update({
            status: 'in_progress',
            progress: 0
            // Keep completed_at and final_score for historical tracking
          })
          .eq('user_email', userEmail)
          .eq('course_id', courseId)

        if (enrollmentError) {
          if (import.meta.env.DEV) console.error('[startSession] ⚠️ Failed to update enrollment for retake:', enrollmentError)
          // Don't fail session creation, but log the issue
        } else {
          if (import.meta.env.DEV) console.log(`[startSession] ✅ Enrollment reset to in_progress for attempt ${attemptNumber}`)
        }
      }

      return { data, error: null }
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // Get session details
  async getSession(sessionId) {
    try {
      const { data, error } = await supabase
        .from('assessment_sessions')
        .select(`
          *,
          question_responses (
            id,
            question_id,
            selected_answer,
            is_correct,
            time_spent,
            confidence_level,
            needs_learning,
            cycle_number,
            answered_at,
            is_ghost_question,
            ghost_question_id
          )
        `)
        .eq('id', sessionId)
        .single()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching session:', error)
      return { data: null, error }
    }
  },

  // Submit answer for a question - RESPECTS DATABASE CONSTRAINT
  async submitAnswer(sessionId, questionId, answerIndex, timeSpent, confidence, variantId = null) {
    try {
      // Get current session to determine cycle number
      const { data: session } = await supabase
        .from('assessment_sessions')
        .select('phase, current_cycle')
        .eq('id', sessionId)
        .single()

      const cycleNumber = session?.current_cycle || 1
      if (import.meta.env.DEV) console.log(`[submitAnswer] Using cycle number ${cycleNumber} for session ${sessionId}`)

      // SIMPLIFIED: Always store what was actually shown/answered in question_id
      const actualQuestionId = variantId || questionId
      let correctAnswer
      let isVariant = !!variantId

      // Get the correct answer from what was actually shown
      if (variantId) {
        // Try questions table first (new variants)
        const { data: variantQuestion } = await supabase
          .from('questions')
          .select('correct_answer, is_variant')
          .eq('id', variantId)
          .single()

        if (variantQuestion) {
          correctAnswer = variantQuestion.correct_answer
          isVariant = true
        } else {
          if (import.meta.env.DEV) console.warn(`[submitAnswer] Variant ${variantId} not found in questions table`)
          // Try to get from main question as fallback
          const { data: mainQ } = await supabase
            .from('questions')
            .select('correct_answer')
            .eq('id', questionId)
            .single()

          correctAnswer = mainQ?.correct_answer ?? 0  // Use nullish coalescing - 0 is valid!
        }
      } else {
        // Main question
        const { data: question } = await supabase
          .from('questions')
          .select('correct_answer')
          .eq('id', questionId)
          .single()
        correctAnswer = question?.correct_answer ?? 0  // Use nullish coalescing - 0 is valid!
        isVariant = false
      }

      // Check if answer is correct
      // IMPORTANT: Set to null for "I need to learn" so it's properly excluded from PHE calculations
      const isCorrect = evaluateAnswer(answerIndex, correctAnswer, confidence)

      // SIMPLIFIED: Always store the actual question answered in question_id
      const insertData = {
        session_id: sessionId,
        question_id: actualQuestionId,  // Always what was shown/answered
        selected_answer: answerIndex,  // Store actual index: -1 for needs_learning, 0+ for selected option
        is_correct: isCorrect,
        time_spent: timeSpent || 0,
        confidence_level: confidence || 0,
        needs_learning: confidence === 0,
        answered_at: new Date().toISOString(),
        cycle_number: cycleNumber
      }

      if (import.meta.env.DEV) console.log(`[submitAnswer] Storing response:`, {
        question_id: actualQuestionId,
        cycle_number: cycleNumber,
        is_correct: isCorrect
      })

      // One response per (session, question, cycle): re-submits (back navigation,
      // refresh, double click) update the existing row instead of inserting a duplicate
      const { data: existingResponse } = await supabase
        .from('question_responses')
        .select('id')
        .eq('session_id', sessionId)
        .eq('question_id', actualQuestionId)
        .eq('cycle_number', cycleNumber)
        .order('answered_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      const { data, error } = await retryOperation(async () => {
        const query = existingResponse
          ? supabase.from('question_responses').update(insertData).eq('id', existingResponse.id)
          : supabase.from('question_responses').insert(insertData)

        const result = await query.select().single()

        if (result.error) throw result.error
        return result
      })

      if (error) {
        if (import.meta.env.DEV) console.error('Database error saving response:', error)
        throw error
      }

      return { data: { ...data, isCorrect }, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error in submitAnswer:', error)
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // CRITICAL: Verify enrollment was updated successfully
  // Returns { success: true/false, enrollmentId, error }
  async verifyEnrollmentUpdate(userEmail, courseId, expectedStatus = 'completed') {
    try {
      const { data: enrollment, error } = await supabase
        .from('enrollments')
        .select('id, status, progress, completed_at')
        .eq('user_email', userEmail)
        .eq('course_id', courseId)
        .single()

      if (error) {
        if (import.meta.env.DEV) console.error('[verifyEnrollmentUpdate] Error fetching enrollment:', error)
        return { success: false, error: 'Failed to fetch enrollment' }
      }

      if (!enrollment) {
        if (import.meta.env.DEV) console.error('[verifyEnrollmentUpdate] No enrollment found')
        return { success: false, error: 'No enrollment found' }
      }

      const isComplete = enrollment.status === expectedStatus &&
                        enrollment.progress === 100 &&
                        enrollment.completed_at !== null

      if (!isComplete) {
        if (import.meta.env.DEV) {
          console.warn('[verifyEnrollmentUpdate] Enrollment not properly updated:', {
            enrollmentId: enrollment.id,
            status: enrollment.status,
            progress: enrollment.progress,
            completed_at: enrollment.completed_at,
            expected: { status: expectedStatus, progress: 100 }
          })
        }
        return {
          success: false,
          enrollmentId: enrollment.id,
          error: `Enrollment incomplete: status=${enrollment.status}, progress=${enrollment.progress}`
        }
      }

      if (import.meta.env.DEV) console.log('[verifyEnrollmentUpdate] ✅ Enrollment verified complete:', enrollment.id)
      return { success: true, enrollmentId: enrollment.id }
    } catch (error) {
      if (import.meta.env.DEV) console.error('[verifyEnrollmentUpdate] Exception:', error)
      return { success: false, error: error.message }
    }
  },

  // Complete assessment session using server-side RPC
  // SECURITY: All score calculations happen server-side to prevent client manipulation
  async completeSession(sessionId) {
    try {
      if (import.meta.env.DEV) console.log('[completeSession] Using server-side RPC for session:', sessionId)

      // Call server-side RPC which handles all scoring and enrollment updates
      const { data: rpcResult, error: rpcError } = await supabase.rpc(
        'complete_assessment_session',
        { p_session_id: sessionId }
      )

      if (rpcError) {
        if (import.meta.env.DEV) console.error('[completeSession] RPC error:', rpcError)
        return { data: null, error: handleSupabaseError(rpcError) }
      }

      if (!rpcResult?.success) {
        if (import.meta.env.DEV) console.error('[completeSession] Server returned error:', rpcResult?.error)
        return { data: null, error: rpcResult?.error || 'Assessment completion failed' }
      }

      if (import.meta.env.DEV) console.log('[completeSession] Server-side completion successful:', {
        sessionId: rpcResult.session_id,
        score: rpcResult.score,
        overallAccuracy: rpcResult.overall_accuracy,
        cyclesToMastery: rpcResult.cycles_to_mastery
      })

      // Fetch the updated session data for return
      const { data: sessionData, error: fetchError } = await supabase
        .from('assessment_sessions')
        .select('*')
        .eq('id', sessionId)
        .single()

      if (fetchError) {
        if (import.meta.env.DEV) console.error('[completeSession] Error fetching updated session:', fetchError)
        // Return the RPC result even if we can't fetch the full session
        return {
          data: {
            id: sessionId,
            score: rpcResult.score,
            overall_accuracy: rpcResult.overall_accuracy,
            cycles_to_mastery: rpcResult.cycles_to_mastery,
            total_questions: rpcResult.total_questions,
            correct_answers: rpcResult.correct_answers,
            status: 'completed'
          },
          error: null
        }
      }

      return { data: sessionData, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('[completeSession] Exception:', error)
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // Get a specific question response for a cycle (defaults to the initial assessment)
  async getQuestionResponse(sessionId, questionId, cycleNumber = 1) {
    try {
      const { data, error } = await supabase
        .from('question_responses')
        .select('*')
        .eq('session_id', sessionId)
        .eq('question_id', questionId)
        .eq('cycle_number', cycleNumber)
        .order('answered_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) {
        if (import.meta.env.DEV) console.error('Error fetching question response:', error)
        return { data: null, error }
      }

      return { data, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error in getQuestionResponse:', error)
      return { data: null, error }
    }
  },

  // Get user's assessment history for a course
  async getUserAssessments(userEmail, courseId) {
    try {
      const { data, error } = await supabase
        .from('assessment_sessions')
        .select('*')
        .eq('user_email', userEmail)
        .eq('course_id', courseId)
        .order('attempt_number', { ascending: false }) // GAP 3.4 FIX: Sort by attempt_number at DB level

      if (error) throw error
      return { data, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching user assessments:', error)
      return { data: null, error }
    }
  },

  // Get responses for review
  async getSessionResponses(sessionId) {
    try {
      if (import.meta.env.DEV) console.log('Fetching responses for session:', sessionId)

      // Get ALL responses for the session (including variants)
      const { data, error } = await supabase
        .from('question_responses')
        .select(`
          *,
          question:questions (
            id,
            question_text,
            options,
            correct_answer,
            explanation,
            topic,
            is_variant,
            parent_id
          )
        `)
        .eq('session_id', sessionId)
        // REMOVED: .eq('is_ghost_question', false) - we want ALL responses
        .order('answered_at', { ascending: true })
      
      if (error) {
        if (import.meta.env.DEV) console.error('Error fetching responses:', error)
        throw error
      }
      
      if (import.meta.env.DEV) console.log('Fetched responses:', data?.length || 0)
      return data || []
    } catch (error) {
      if (import.meta.env.DEV) console.error('Error fetching session responses:', error)
      return []
    }
  },

  // Create a new session (generic)
  async createSession(sessionData) {
    try {
      const { data, error } = await supabase
        .from('assessment_sessions')
        .insert([sessionData])
        .select()
        .single()
      
      if (error) throw error
      return { data, error: null }
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // Update session (including phase timing)
  async updateSession(sessionId, updates) {
    try {
      // CRITICAL FIX: Map frontend naming to database columns FIRST
      // This must happen before any other logic that might depend on these values
      if (updates.learning_time_spent !== undefined) {
        updates.learning_phase_time = updates.learning_time_spent
        delete updates.learning_time_spent
      }
      if (updates.reassessment_time_spent !== undefined) {
        updates.reassessment_phase_time = updates.reassessment_time_spent
        delete updates.reassessment_time_spent
      }

      // NOW check for reassessment phase and increment cycle
      // This must happen AFTER property mapping to ensure the cycle increment is preserved
      if (updates.phase === 'reassessment') {
        const { data: currentSession } = await supabase
          .from('assessment_sessions')
          .select('current_cycle')
          .eq('id', sessionId)
          .single()

        updates.current_cycle = (currentSession?.current_cycle || 1) + 1
        if (import.meta.env.DEV) console.log(`[assessmentService] Incrementing cycle from ${currentSession?.current_cycle || 1} to ${updates.current_cycle} for session ${sessionId}`)
      }

      // If phase timing is included, also calculate and update total active time
      if (updates.learning_phase_time || updates.reassessment_phase_time) {
        const { data: currentSession } = await supabase
          .from('assessment_sessions')
          .select('initial_phase_time, learning_phase_time, reassessment_phase_time')
          .eq('id', sessionId)
          .single()

        if (currentSession) {
          // Calculate total active time
          updates.total_active_time = 
            (currentSession.initial_phase_time || 0) +
            (updates.learning_phase_time || currentSession.learning_phase_time || 0) +
            (updates.reassessment_phase_time || currentSession.reassessment_phase_time || 0)
        }
      }
      
      if (import.meta.env.DEV) console.log(`[updateSession] Updating session ${sessionId} with:`, updates)

      const { data, error } = await supabase
        .from('assessment_sessions')
        .update(updates)
        .eq('id', sessionId)
        .select()
        .single()

      if (error) {
        if (import.meta.env.DEV) console.error(`[updateSession] Error updating session:`, error)
        throw error
      }

      if (import.meta.env.DEV) console.log(`[updateSession] Session updated successfully:`, data)
      return { data, error: null }
    } catch (error) {
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  // Struggle tracking removed - now calculated directly from question_responses table
  // See ReassessmentReview.jsx for implementation

  /**
   * Abandon current session
   * Marks session as abandoned and clears localStorage
   * User loses all progress from this attempt
   */
  async abandonSession(sessionId) {
    try {
      const { data, error } = await supabase
        .from('assessment_sessions')
        .update({
          status: 'abandoned',
          phase: 'abandoned'
        })
        .eq('id', sessionId)
        .select()
        .single()

      if (error) throw error

      // Clear localStorage
      localStorage.removeItem('currentAssessmentSession')
      localStorage.removeItem('sessionStartTime')

      if (import.meta.env.DEV) console.log('[abandonSession] Session abandoned:', sessionId)
      return { data, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('[abandonSession] Error:', error)
      return { data: null, error: handleSupabaseError(error) }
    }
  },

  /**
   * Get last completed session for a user/course
   * Used to show "View Previous Results" option
   */
  async getLastCompletedSession(userEmail, courseId) {
    try {
      const { data, error } = await supabase
        .from('assessment_sessions')
        .select('*')
        .eq('user_email', userEmail)
        .eq('course_id', courseId)
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(1)
        .single()

      if (error && error.code === 'PGRST116') {
        // No completed sessions found
        return { data: null, error: null }
      }

      if (error) throw error

      return { data, error: null }
    } catch (error) {
      if (import.meta.env.DEV) console.error('[getLastCompletedSession] Error:', error)
      return { data: null, error: handleSupabaseError(error) }
    }
  }
}
